import type { ApiResource } from "../../shared/api/resource.js";
import { minorUnitsToUsd } from "../../shared/lib/usd.js";

export const botStates = [
  "draft",
  "ready",
  "backtesting",
  "stopped",
  "starting",
  "live",
  "stopping",
  "archived",
  "error",
] as const;

export type BotState = (typeof botStates)[number];

export interface BotView {
  readonly activeRevisionId: string;
  readonly createdAt: string;
  readonly id: string;
  readonly liveBrokerAccountId?: string;
  readonly name: string;
  readonly state: BotState;
  readonly symbol: string;
  readonly updatedAt: string;
  readonly version: number;
}

export interface BotConfiguration {
  readonly execution: {
    readonly defaultOrderType: "limit" | "market";
    readonly timeInForce: "day" | "gtc";
  };
  readonly positionSizing: {
    readonly kind: "fixed-notional" | "fixed-quantity" | "percent-of-equity";
    readonly value: string;
  };
  readonly risk: {
    readonly allowShort: boolean;
    readonly maxPositionNotionalUsd: string;
    readonly maxPositionQuantity: string;
  };
  readonly schedule: {
    readonly exchange: "XNAS" | "XNYS";
    readonly regularSessionOnly: boolean;
    readonly timeZone: "America/New_York";
  };
  readonly strategy: {
    readonly kind: string;
    readonly parameters: Record<string, unknown>;
    readonly version: string;
  };
  readonly symbol: string;
}

export interface BotRevisionView extends BotConfiguration {
  readonly botId: string;
  readonly createdAt: string;
  readonly id: string;
  readonly revision: number;
}

export type BacktestInterval = "1m" | "5m" | "15m" | "1h" | "1d";

const backtestIntervalRank: Readonly<Record<BacktestInterval, number>> = {
  "1m": 0,
  "5m": 1,
  "15m": 2,
  "1h": 3,
  "1d": 4,
};

function collectRequiredIntervals(value: unknown, intervals: BacktestInterval[]): void {
  if (Array.isArray(value)) {
    for (const child of value) collectRequiredIntervals(child, intervals);
    return;
  }
  if (value === null || typeof value !== "object") return;
  const item = value as Readonly<Record<string, unknown>>;
  if (item.kind === "named-stock-field" && typeof item.name === "string") {
    if (item.name.endsWith("_1min")) intervals.push("1m");
    else if (item.name.endsWith("_5min")) intervals.push("5m");
    else if (item.name.endsWith("_15min")) intervals.push("15m");
    else if (item.name.endsWith("_daily")) intervals.push("1d");
  }
  for (const child of Object.values(item)) collectRequiredIntervals(child, intervals);
}

export function requiredBacktestInterval(
  revision: BotRevisionView | undefined,
): BacktestInterval | undefined {
  if (revision === undefined) return undefined;
  // Calendar-based monthly values do not require a fixed source interval.
  if (isMonthlyStrategy(revision.strategy)) return undefined;
  const intervals: BacktestInterval[] = [];
  collectRequiredIntervals(revision.strategy.parameters, intervals);
  return intervals.sort(
    (left, right) => backtestIntervalRank[left] - backtestIntervalRank[right],
  )[0];
}

export function isMonthlyStrategy(strategy: BotConfiguration["strategy"] | undefined): boolean {
  return (
    strategy?.kind === "monthly-trend-cash" ||
    (strategy?.kind === "decision-graph" &&
      record(strategy.parameters.calendar).kind === "month-end")
  );
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown, fallback = ""): string {
  return typeof value === "string" || typeof value === "number" ? String(value) : fallback;
}

function numberValue(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function booleanValue(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === "string" && allowed.includes(value as T) ? (value as T) : fallback;
}

export function botView(resource: ApiResource): BotView {
  const account = text(resource.liveBrokerAccountId);
  return {
    activeRevisionId: text(resource.activeRevisionId),
    createdAt: text(resource.createdAt),
    id: resource.id,
    ...(account === "" ? {} : { liveBrokerAccountId: account }),
    name: text(resource.name, "Unnamed bot"),
    state: oneOf(resource.state, botStates, "error"),
    symbol: text(resource.symbol, "—"),
    updatedAt: text(resource.updatedAt),
    version: numberValue(resource.version),
  };
}

export function botRevisionView(resource: ApiResource): BotRevisionView {
  const strategy = record(resource.strategy);
  const positionSizing = record(resource.positionSizing);
  const risk = record(resource.risk);
  const schedule = record(resource.schedule);
  const execution = record(resource.execution);
  const notional = record(risk.maxPositionNotional);
  return {
    botId: text(resource.botId),
    createdAt: text(resource.createdAt),
    execution: {
      defaultOrderType: oneOf(execution.defaultOrderType, ["market", "limit"], "market"),
      timeInForce: oneOf(execution.timeInForce, ["day", "gtc"], "day"),
    },
    id: resource.id,
    positionSizing: {
      kind: oneOf(
        positionSizing.kind,
        ["fixed-quantity", "fixed-notional", "percent-of-equity"],
        "fixed-quantity",
      ),
      value: text(positionSizing.value, "1"),
    },
    revision: numberValue(resource.revision),
    risk: {
      allowShort: booleanValue(risk.allowShort),
      maxPositionNotionalUsd: minorUnitsToUsd(text(notional.minorUnits)),
      maxPositionQuantity: text(risk.maxPositionQuantity),
    },
    schedule: {
      exchange: oneOf(schedule.exchange, ["XNYS", "XNAS"], "XNYS"),
      regularSessionOnly: booleanValue(schedule.regularSessionOnly, true),
      timeZone: "America/New_York",
    },
    strategy: {
      kind: text(strategy.kind),
      parameters: record(strategy.parameters),
      version: text(strategy.version, "1"),
    },
    symbol: text(resource.symbol),
  };
}

export const defaultBotConfiguration: BotConfiguration = {
  execution: { defaultOrderType: "market", timeInForce: "day" },
  positionSizing: { kind: "fixed-quantity", value: "1" },
  risk: { allowShort: false, maxPositionNotionalUsd: "", maxPositionQuantity: "" },
  schedule: {
    exchange: "XNYS",
    regularSessionOnly: true,
    timeZone: "America/New_York",
  },
  strategy: { kind: "", parameters: {}, version: "1" },
  symbol: "",
};

export function lifecycleActions(
  state: BotState,
): readonly ("archive" | "ready" | "start" | "stop")[] {
  switch (state) {
    case "draft":
      return ["ready", "archive"];
    case "ready":
    case "stopped":
      return ["start", "archive"];
    case "starting":
    case "live":
      return ["stop"];
    case "error":
      return ["ready", "stop", "archive"];
    case "archived":
    case "backtesting":
    case "stopping":
      return [];
  }
}
